import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Input,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Select,
  Checkbox,
  VStack,
  Text,
  FormErrorMessage,
  Image,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomDatePicker from "./CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import ApiService from "../services/ApiService";
import { AuthContext } from "../context/AuthContext";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard,
  faLocationDot,
  faImage,
  faHatCowboy,
} from "@fortawesome/free-solid-svg-icons";

const EditFarmerProfile = () => {
  const navigate = useNavigate();
  const { profile, sessionId } = useContext(AuthContext) || {};
  const [formData, setFormData] = useState(null); // เริ่มต้นเป็น null เพื่อรอข้อมูล
  const [errors, setErrors] = useState({});
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 1. ดึงข้อมูลผู้ใช้เมื่อคอมโพเนนต์โหลด
  useEffect(() => {
    if (profile?.FarmerID) {
      const fetchFarmerData = async () => {
        setIsLoading(true);
        try {
          const response = await ApiService({
            url: `/getFarmerProfileByID/${profile.FarmerID}`,
            method: "GET",
            sessionId: sessionId,
          });

          const data = response.data;
          // แปลงค่า BirthDate ที่ได้จาก API (String) ให้เป็น Date object
          // และตรวจสอบข้อมูล object ที่ซ้อนกันอยู่ให้มีค่าเริ่มต้นเสมอ
          setFormData({
            ...data,
            BirthDate: data.BirthDate || null,
            FarmAddress: data.FarmAddress || { AddressNo: "", AddressMoo: "", AddressRoad: "", AddressSubdistrict: "", AddressDistrict: "", AddressProvince: "", AddressZipcode: "" },
            Livestock: data.Livestock || { BeefCattle: 0, DairyCattle: 0, Buffalo: 0, Swine: 0, Chicken: 0, Duck: 0, Goat: 0, Sheep: 0, Quail: 0, Dog: 0, Cat: 0, Other: 0 }
          });

        } catch (error) {
          console.error("Failed to fetch farmer data", error);
          alert("ไม่สามารถโหลดข้อมูลเกษตรกรได้");
        } finally {
          setIsLoading(false);
        }
      };
      fetchFarmerData();
    }
  }, [profile, sessionId]);

  // useEffect สำหรับจัดการการคัดลอกที่อยู่
  useEffect(() => {
    if (!formData) return; // ถ้ายังไม่มีข้อมูล ไม่ต้องทำอะไร

    if (useSameAddress) {
      setFormData((prev) => ({
        ...prev,
        FarmAddress: {
          AddressNo: prev.AddressNo,
          AddressMoo: prev.AddressMoo,
          AddressRoad: prev.AddressRoad,
          AddressSubdistrict: prev.AddressSubdistrict,
          AddressDistrict: prev.AddressDistrict,
          AddressProvince: prev.AddressProvince,
          AddressZipcode: prev.AddressZipcode,
        },
      }));
    }
  }, [useSameAddress, formData?.AddressNo]); // ตรวจสอบเมื่อ useSameAddress หรือที่อยู่หลักเปลี่ยน


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFarmAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      FarmAddress: { ...prev.FarmAddress, [name]: value },
    }));
  };

  const handleLivestockChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      Livestock: { ...prev.Livestock, [name]: value },
    }));
  };


  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({ ...formData, ProfileImage: reader.result });
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const isFormValid = () => {
    // ตัดการตรวจสอบรหัสผ่านและ privacy check ออก
    return (
      formData.FirstName?.trim() !== "" &&
      formData.LastName?.trim() !== "" &&
      formData.IDCard?.trim() !== "" &&
      formData.BirthDate !== null &&
      formData.Email?.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone?.trim() !== "" &&
      Object.keys(errors).length === 0
    );
  };

  // 2. ฟังก์ชันสำหรับบันทึกการแก้ไข
  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง");
      return;
    }

    // จัดการข้อมูลก่อนส่ง
    const submitData = {
        ...formData,
        // ส่ง FarmerID ไปด้วยเพื่อให้ Backend รู้ว่าต้องอัปเดตใคร
        FarmerID: profile.FarmerID, 
        ProfileImage: formData.ProfileImage || "",
    };
    
    // ไม่ต้องแปลงวันที่อีก เพราะ CustomDatePicker จัดการให้แล้ว
    // หาก API ต้องการ Format เฉพาะ ให้แปลงที่นี่

    try {
      await ApiService({
        method: "POST", // หรือ 'PUT' ตามที่ API กำหนด
        url: "/updateFarmer",
        data: submitData,
        sessionId: sessionId,
      });
      alert("บันทึกการแก้ไขสำเร็จ!");
      navigate(-1); // กลับไปหน้าโปรไฟล์
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + error.message);
    }
  };

  if (isLoading || !formData) {
    return (
      <Flex justify="center" align="center" height="80vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // --- START COPY FROM RegistrationFarmer ---
  const livestockTypes = [
    { key: "BeefCattle", label: "โคเนื้อ" }, { key: "DairyCattle", label: "โคนม" }, { key: "Buffalo", label: "กระบือ" },
    { key: "Swine", label: "สุกร" }, { key: "Chicken", label: "ไก่" }, { key: "Duck", label: "เป็ด" },
    { key: "Goat", label: "แพะ" }, { key: "Sheep", label: "แกะ" }, { key: "Quail", label: "นกกระทา" },
    { key: "Dog", label: "สุนัข" }, { key: "Cat", label: "แมว" }, { key: "Other", label: "สัตว์อื่นๆ" },
  ];

  const farmAddressFields = [
    { key: "AddressNo", label: "เลขที่" }, { key: "AddressMoo", label: "หมู่" }, { key: "AddressRoad", label: "ถนน" },
    { key: "AddressSubdistrict", label: "ตำบล" }, { key: "AddressDistrict", label: "อำเภอ" },
    { key: "AddressProvince", label: "จังหวัด" }, { key: "AddressZipcode", label: "รหัสไปรษณีย์" },
  ];
  // --- END COPY ---

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="แก้ไขข้อมูลเกษตรกร" />

      {/* Box 1: ข้อมูลทั่วไป */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader title="ข้อมูลทั่วไป" bgColor="iTeal.600" icon={faAddressCard} />
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          {["FirstName", "LastName", "IDCard", "Gender", "BirthDate"].map((key) => (
            <GridItem colSpan={1} key={key}>
              <FormControl isRequired isInvalid={!!errors[key]}>
                <FormLabel fontSize="xl" color="gray">
                  { key === "FirstName" ? "ชื่อ" : key === "LastName" ? "นามสกุล" : key === "IDCard" ? "เลขบัตรประชาชน" : key === "Gender" ? "เพศ" : "วันเกิด (วัน/เดือน/ปี พ.ศ.)" }
                  <Text as="span" color="red"> *</Text>
                </FormLabel>
                {key === "Gender" ? (
                  <Select name={key} value={formData[key]} onChange={handleInputChange}>
                    <option value="M">ชาย</option>
                    <option value="F">หญิง</option>
                  </Select>
                ) : key === "BirthDate" ? (
                  <CustomDatePicker name={key} value={formData.BirthDate} onChange={handleInputChange} />
                ) : (
                  <Input name={key} value={formData[key]} onChange={handleInputChange} isDisabled={key === "IDCard"} />
                )}
                <FormErrorMessage>{errors[key]}</FormErrorMessage>
              </FormControl>
            </GridItem>
          ))}
        </Grid>
      </Box>

      {/* Box 2: ข้อมูลการติดต่อ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader title="ข้อมูลการติดต่อ" bgColor="iBlue.600" icon={faLocationDot} />
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          {[ "AddressNo", "AddressMoo", "AddressRoad", "AddressSubdistrict", "AddressDistrict", "AddressProvince", "AddressZipcode", "Email", "Phone"].map((key) => (
            <GridItem colSpan={1} key={key}>
              <FormControl isRequired={["Email", "Phone"].includes(key)} isInvalid={!!errors[key]}>
                <FormLabel fontSize="xl" color="gray">
                  { key === "Email" ? "อีเมลล์" : key === "Phone" ? "เบอร์โทรศัพท์" : key === "AddressNo" ? "เลขที่" : key === "AddressMoo" ? "หมู่" : key === "AddressRoad" ? "ถนน" : key === "AddressSubdistrict" ? "ตำบล" : key === "AddressDistrict" ? "อำเภอ" : key === "AddressProvince" ? "จังหวัด" : key === "AddressZipcode" ? "รหัสไปรษณีย์" : "ที่อยู่"}
                  {["Email", "Phone"].includes(key) ? (<Text as="span" color="red"> *</Text>) : null}
                </FormLabel>
                <Input name={key} value={formData[key]} onChange={handleInputChange} />
                <FormErrorMessage>{errors[key]}</FormErrorMessage>
              </FormControl>
            </GridItem>
          ))}
        </Grid>
      </Box>
      
      {/* Box 3: ข้อมูลปศุสัตว์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader title="ข้อมูลปศุสัตว์" bgColor="iGreen.600" icon={faHatCowboy} />
          <VStack spacing={5} p={5} align="flex-start">
              <Grid templateColumns="repeat(3, 1fr)" gap={6} w="100%">
                  <GridItem colSpan={2}>
                      <FormControl>
                      <FormLabel fontSize="xl" color="gray">ชื่อฟาร์ม / ชื่อบริษัท</FormLabel>
                      <Input name="FarmName" value={formData.FarmName} onChange={handleInputChange} />
                      </FormControl>
                  </GridItem>
              </Grid>
              <FormControl>
                  <FormLabel fontSize="xl" color="gray">สถานที่ตั้ง</FormLabel>
                  <Checkbox isChecked={useSameAddress} onChange={(e) => setUseSameAddress(e.target.checked)} size="lg">
                      ใช้ที่อยู่เดียวกับข้อมูลการติดต่อ
                  </Checkbox>
              </FormControl>
              <Grid templateColumns="repeat(3, 1fr)" gap={6} w="100%">
              {farmAddressFields.map((field) => (
                  <GridItem colSpan={1} key={field.key}>
                      <FormControl>
                          <FormLabel color="gray.600">{field.label}</FormLabel>
                          <Input name={field.key} value={formData.FarmAddress[field.key]} onChange={handleFarmAddressChange} isDisabled={useSameAddress} />
                      </FormControl>
                  </GridItem>
              ))}
              </Grid>
              <FormControl>
              <FormLabel fontSize="xl" color="gray" mb={4}>จำนวนปศุสัตว์</FormLabel>
              <Grid templateColumns="repeat(4, 1fr)" gap={5}>
                {livestockTypes.map((animal) => (
                  <GridItem key={animal.key}>
                    <FormControl>
                      <FormLabel color="gray.600">{animal.label}</FormLabel>
                      <NumberInput min={0} value={formData.Livestock[animal.key]} onChange={(valueString) => handleLivestockChange(animal.key, parseInt(valueString) || 0)}>
                        <NumberInputField />
                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </GridItem>
                ))}
              </Grid>
              </FormControl>
          </VStack>
      </Box>

      {/* Box 4: รูปโปรไฟล์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader title="รูปโปรไฟล์" bgColor="iOrange.600" icon={faImage}/>
        <Box {...getRootProps()} borderWidth={2} borderStyle="dashed" borderRadius="md" w="70%" p={5} textAlign="center" cursor="pointer" mt={5}>
          <Input {...getInputProps()} />
          {formData.ProfileImage ? ( <Image src={formData.ProfileImage} alt="Profile Preview" maxH="200px" mx="auto"/> ) : 
          ( <Text h={20}>ลากและวางรูปภาพที่นี่ หรือคลิกเพื่อเลือกไฟล์</Text> )}
        </Box>
      </Box>

      {/* ส่วนของปุ่ม */}
      <Flex justify="flex-end" p={5} m={5}>
        <Button colorScheme="gray" variant="outline" mr={4} onClick={() => navigate(-1)}>
          ยกเลิก
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit} isDisabled={!isFormValid()}>
          บันทึกการแก้ไข
        </Button>
      </Flex>
    </Box>
  );
};

export default EditFarmerProfile;